import React, { useState, useEffect, useRef } from "react";
import BackIcon from "../../images/BackIcon";
import { webNavigate, webLoadingTrue, webLoadingFalse } from "../../services/navigate.service";
import { importCsv, ImportPayrollPayload } from "../../services/payrollPayments.service";
import PayrollPaymentModal from "./PayrollPaymentModal";
import { getTransactionMonth } from "../../services/purchases.services";
import { showToast } from "../../services/toast.service";
import { showApiError } from "../../services/apiError";
import AxiosInstance from "../../config/AxiosInstance";
import { DropdownSearch } from "../../components/DropdownSearch";
import { useAsyncDropdown } from "../../hook/useAsyncDropdown";

interface ChartOfAccount {
  id: string;
  accountCode: string;
  accountName: string;
}

const PayrollPaymentUpload = () => {
  const preLanguage = localStorage.getItem("language") || "en";
  const companyId = localStorage.getItem("companyId") || "";
  const locationId = localStorage.getItem("locationId") || "";
  const [loading, setLoading] = useState(false);
  const language = (window as any).languageContext?.language;
  const [file, setFile] = useState<File | null>(null);
  const [listMonths, setListMonths] = useState<any[]>([]);
  const [selectedMonthId, setSelectedMonthId] = useState("");
  const [selectedMonthLabel, setSelectedMonthLabel] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [description, setDescription] = useState("");
  const [bankCoaId, setBankCoaId] = useState("");
  const [bankCoaLabel, setBankCoaLabel] = useState("");
  const [salaryCoaId, setSalaryCoaId] = useState("");
  const [salaryCoaLabel, setSalaryCoaLabel] = useState("");
  const [isMonthOpen, setIsMonthOpen] = useState(false);
  const [modalBatchId, setModalBatchId] = useState<string | null>(null);
  const monthRef = useRef<HTMLDivElement>(null);
  const bankCoaRef = useRef<HTMLDivElement>(null);
  const salaryCoaRef = useRef<HTMLDivElement>(null);
  const [bankCoaOpen, setBankCoaOpen] = useState(false);
  const [salaryCoaOpen, setSalaryCoaOpen] = useState(false);
  const PAGE_SIZE_COA = 50;

  const coaFetch = React.useCallback(
    async ({
      page,
      search,
      limit,
      signal,
    }: {
      page: number;
      search: string;
      limit: number;
      signal?: AbortSignal;
    }) => {
      const response = await AxiosInstance.get("/api/system-accounts", {
        params: {
          page: page.toString(),
          limit: limit.toString(),
          ...(search.trim() ? { search: search.trim() } : {}),
        },
        signal,
      });
      const accounts = response.data?.data ?? response.data ?? [];
      const transformed: ChartOfAccount[] = (Array.isArray(accounts) ? accounts : []).map((acc: any) => ({
        id: acc.id,
        accountCode: acc.accountCode ?? "",
        accountName: acc.accountName ?? "",
      }));
      return {
        data: transformed,
        hasMore: Array.isArray(accounts) && accounts.length >= limit,
      };
    },
    []
  );
  const asyncBankCoa = useAsyncDropdown<ChartOfAccount>({
    fetchFn: coaFetch,
    limit: PAGE_SIZE_COA,
    getItemId: (item) => item.id,
    resetDeps: [companyId],
  });
  const asyncSalaryCoa = useAsyncDropdown<ChartOfAccount>({
    fetchFn: coaFetch,
    limit: PAGE_SIZE_COA,
    getItemId: (item) => item.id,
    resetDeps: [companyId],
  });

  useEffect(() => {
    getTransactionMonth(locationId, companyId)
      .then((res: any) => {
        const raw = res?.data ?? [];
        const filtered = (Array.isArray(raw) ? raw : [])
          .filter((m: any) => !m.isDeleted)
          .sort((a: any, b: any) => new Date(b.fromDate).getTime() - new Date(a.fromDate).getTime());
        setListMonths(filtered);
        if (filtered.length > 0 && !selectedMonthId) {
          const first = filtered[0];
          setSelectedMonthId(first.id);
          setSelectedMonthLabel(`${first.transactionYear} - ${first.monthSequence}`);
        }
      })
      .catch(() => {});
  }, [companyId, locationId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      showToast({ message: "Please select a CSV file", type: "error" });
      return;
    }
    if (!selectedMonthId || !bankCoaId || !salaryCoaId) {
      showToast({ message: "Please fill required fields: Transaction month, Bank COA, Salary payable COA", type: "error" });
      return;
    }
    setLoading(true);
    webLoadingTrue();
    try {
      const payload: ImportPayrollPayload = {
        transactionMonthsId: selectedMonthId,
        ...(paymentDate ? { paymentDate } : {}),
        payrollPeriodYear: year,
        payrollPeriodMonth: month,
        description: description || undefined,
        bankChartOfAccountId: bankCoaId,
        salaryPayableChartOfAccountId: salaryCoaId,
      };
      const result = await importCsv(file, payload);
      showToast({ message: `Imported ${result.lineCount} rows. Total: ${Number(result.totalAmount).toFixed(2)}`, type: "success" });
      setModalBatchId(result.id);
    } catch (err: any) {
      showApiError(err, { language, titleFallback: "Import failed" });
    } finally {
      setLoading(false);
      webLoadingFalse();
    }
  };

  return (
    <div className="font-inter">
      <div className="flex items-center gap-4 mb-6">
        <div
          className="cursor-pointer p-2 rounded-full hover:bg-[#EBF0F0]"
          onClick={() => webNavigate(`/${preLanguage}/payroll-payments`)}
        >
          <BackIcon />
        </div>
        <p style={{ color: "#222525" }} className="text-[24px] leading-[130%] font-medium">
          Upload payroll CSV
        </p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-xl space-y-5">
        <div>
          <label className="block text-[14px] font-medium mb-2" style={{ color: "#4A5353" }}>
            CSV file *
          </label>
          <input
            type="file"
            accept=".csv"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="block w-full text-[14px] border border-[#EBF0F0] rounded-xl px-4 py-3"
          />
        </div>

        <div>
          <label className="block text-[14px] font-medium mb-2" style={{ color: "#4A5353" }}>
            Transaction month *
          </label>
          <div ref={monthRef} className="relative">
            <button
              type="button"
              onClick={() => setIsMonthOpen(!isMonthOpen)}
              className="w-full text-left border border-[#EBF0F0] rounded-xl px-4 py-3 text-[14px] flex items-center justify-between"
              style={{ color: "#222525" }}
            >
              {selectedMonthLabel || "Select month"}
              <span className="text-[#4A5353]">▼</span>
            </button>
            {isMonthOpen && (
              <div
                className="absolute z-10 mt-1 w-full bg-white border border-[#EBF0F0] rounded-xl shadow-lg max-h-60 overflow-auto"
              >
                {listMonths.map((m: any) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => {
                      setSelectedMonthId(m.id);
                      setSelectedMonthLabel(`${m.transactionYear} - ${m.monthSequence}`);
                      setIsMonthOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 text-[14px] hover:bg-[#F5F7F7]"
                    style={{ color: "#222525" }}
                  >
                    {m.transactionYear} - {m.monthSequence}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div>
          <label className="block text-[14px] font-medium mb-2" style={{ color: "#4A5353" }}>
            Payment date (optional)
          </label>
          <input
            type="date"
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            className="w-full border border-[#EBF0F0] rounded-xl px-4 py-3 text-[14px]"
            style={{ color: "#222525" }}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-[14px] font-medium mb-2" style={{ color: "#4A5353" }}>
              Period year
            </label>
            <input
              type="number"
              min={2000}
              max={2100}
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="w-full border border-[#EBF0F0] rounded-xl px-4 py-3 text-[14px]"
              style={{ color: "#222525" }}
            />
          </div>
          <div>
            <label className="block text-[14px] font-medium mb-2" style={{ color: "#4A5353" }}>
              Period month
            </label>
            <input
              type="number"
              min={1}
              max={12}
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="w-full border border-[#EBF0F0] rounded-xl px-4 py-3 text-[14px]"
              style={{ color: "#222525" }}
            />
          </div>
        </div>

        <div>
          <label className="block text-[14px] font-medium mb-2" style={{ color: "#4A5353" }}>
            Description
          </label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Paga Shkurt 2026"
            className="w-full border border-[#EBF0F0] rounded-xl px-4 py-3 text-[14px]"
            style={{ color: "#222525" }}
          />
        </div>

        <div>
          <label className="block text-[14px] font-medium mb-2" style={{ color: "#4A5353" }}>
            Bank (chart of account) *
          </label>
          <div ref={bankCoaRef}>
            <DropdownSearch<ChartOfAccount>
              refKey="bankCoa"
              refs={{ bankCoa: bankCoaRef }}
              openStates={{ bankCoa: bankCoaOpen }}
              open={(key) => {
                setBankCoaOpen(true);
                asyncBankCoa.loadInitial();
              }}
              close={(key) => setBankCoaOpen(false)}
              data={asyncBankCoa.items}
              labelExtractor={(item) => `${item.accountCode} - ${item.accountName}`}
              value={bankCoaOpen ? asyncBankCoa.searchTerm : bankCoaLabel}
              setValue={asyncBankCoa.setSearchTerm}
              onSelect={(item) => {
                setBankCoaId(item.id);
                setBankCoaLabel(`${item.accountCode} - ${item.accountName}`);
                setBankCoaOpen(false);
              }}
              onSearch={asyncBankCoa.setSearchTerm}
              placeholder="Select bank account"
              hasMore={asyncBankCoa.hasMore}
              onLoadMore={asyncBankCoa.loadMore}
              loading={asyncBankCoa.isLoading}
              serverSideSearch
            />
          </div>
        </div>

        <div>
          <label className="block text-[14px] font-medium mb-2" style={{ color: "#4A5353" }}>
            Salary payable (chart of account) *
          </label>
          <div ref={salaryCoaRef}>
            <DropdownSearch<ChartOfAccount>
              refKey="salaryCoa"
              refs={{ salaryCoa: salaryCoaRef }}
              openStates={{ salaryCoa: salaryCoaOpen }}
              open={(key) => {
                setSalaryCoaOpen(true);
                asyncSalaryCoa.loadInitial();
              }}
              close={(key) => setSalaryCoaOpen(false)}
              data={asyncSalaryCoa.items}
              labelExtractor={(item) => `${item.accountCode} - ${item.accountName}`}
              value={salaryCoaOpen ? asyncSalaryCoa.searchTerm : salaryCoaLabel}
              setValue={asyncSalaryCoa.setSearchTerm}
              onSelect={(item) => {
                setSalaryCoaId(item.id);
                setSalaryCoaLabel(`${item.accountCode} - ${item.accountName}`);
                setSalaryCoaOpen(false);
              }}
              onSearch={asyncSalaryCoa.setSearchTerm}
              placeholder="Select salary payable account"
              hasMore={asyncSalaryCoa.hasMore}
              onLoadMore={asyncSalaryCoa.loadMore}
              loading={asyncSalaryCoa.isLoading}
              serverSideSearch
            />
          </div>
        </div>

        <div className="flex gap-3 pt-4">
          <button
            type="button"
            onClick={() => webNavigate(`/${preLanguage}/payroll-payments`)}
            className="px-6 py-3 rounded-full text-[14px] font-medium border border-[#EBF0F0]"
            style={{ color: "#4A5353" }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-3 rounded-full text-[14px] font-medium text-white"
            style={{ backgroundColor: "#0F7679" }}
          >
            {loading ? "Uploading..." : "Upload"}
          </button>
        </div>
      </form>

      {modalBatchId && (
        <PayrollPaymentModal
          batchId={modalBatchId}
          onClose={() => {
            setModalBatchId(null);
            webNavigate(`/${preLanguage}/payroll-payments`);
          }}
        />
      )}
    </div>
  );
};

PayrollPaymentUpload.displayName = "PayrollPaymentUpload";
export default PayrollPaymentUpload;
